/**
 * Schema conditional visibility evaluation.
 * Shared utility for evaluating visibleWhen predicates on schema fields and documents.
 *
 * Predicate format: { field, operator, value }
 * Operators: eq, neq, in, notIn
 *
 * Field references:
 *   - Plain name (e.g. "citizenshipType") → resolves from itemData (current repeatable item)
 *   - Dot notation (e.g. "businessInfo.countryOfIncorporation") → resolves from fullData (entire app)
 */

/**
 * Evaluate a single visibleWhen predicate.
 *
 * @param {Object|null} predicate - { field, operator, value } or null/undefined (always visible)
 * @param {Object} itemData - Current item data (repeatable section item, or section data for flat steps)
 * @param {Object} fullData - Full application data (for cross-step references)
 * @returns {boolean}
 */
export function evaluateVisibility(predicate, itemData = {}, fullData = {}) {
  if (!predicate) return true;

  const { field, operator, value } = predicate;
  if (!field || !operator) return true;

  // Resolve field value
  let fieldValue;
  if (field.includes(".")) {
    const parts = field.split(".");
    let obj = fullData;
    for (const part of parts) {
      obj = obj?.[part];
    }
    fieldValue = obj;
  } else {
    fieldValue = itemData[field];
  }

  switch (operator) {
    case "eq":
      return fieldValue === value;
    case "neq":
      return fieldValue !== value;
    case "in":
      return Array.isArray(value) && value.includes(fieldValue);
    case "notIn":
      return Array.isArray(value) && !value.includes(fieldValue);
    default:
      return true;
  }
}

/**
 * Filter a list of fields to only those that are visible given current data.
 *
 * @param {Array} fields - Schema field definitions
 * @param {Object} itemData - Current item or section data
 * @param {Object} fullData - Full application data
 * @returns {Array} Visible fields
 */
export function getVisibleFields(fields, itemData = {}, fullData = {}) {
  return fields.filter((f) =>
    evaluateVisibility(f.visibleWhen, itemData, fullData)
  );
}

/**
 * Filter document schema to only visible documents, expanding per-owner docs.
 *
 * @param {Array} documentSchema - Schema documents array
 * @param {Object} fullData - Full application data
 * @returns {Array} Expanded document definitions with labels including owner names
 */
export function getVisibleDocuments(documentSchema, fullData = {}) {
  const owners = fullData.owners || [];
  const result = [];

  for (const docDef of documentSchema) {
    // Check document-level visibility
    if (!evaluateVisibility(docDef.visibleWhen, {}, fullData)) continue;

    if (docDef.perOwner) {
      // Expand per-owner documents
      const matchingOwners = owners.filter((owner) =>
        evaluateVisibility(docDef.ownerFilter, owner, fullData)
      );

      for (const owner of matchingOwners) {
        const ownerName = owner.fullName || "Owner";
        result.push({
          ...docDef,
          type: `${docDef.type}_${owner.ownerId}`,
          label: `${ownerName}'s ${docDef.label}`,
          description: `${docDef.description} (${ownerName})`,
          _ownerId: owner.ownerId,
        });
      }
    } else {
      result.push(docDef);
    }
  }

  return result;
}
